import type { OverdueItemEntry } from "#/types/report";
import { formatCurrency } from "#/utils/formatCurrency";
import { sanitizeItemStatusLabel } from "#/utils/StringUtils";
import { Button, Table } from "@mantine/core";
import { IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import ReportLink from "./ReportLink";

const DEFAULT_VISIBLE = 10;

interface CollapsibleItemTableProps {
  items: OverdueItemEntry[];
  showDaysSinceLastScan?: boolean;
}

const CollapsibleItemTable = ({
  items,
  showDaysSinceLastScan = false,
}: CollapsibleItemTableProps) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const hasMore = items.length > DEFAULT_VISIBLE;

  return (
    <div className="flex flex-col gap-2" data-collapsible-table>
      <Table striped highlightOnHover withTableBorder>
        <Table.Thead>
          <Table.Tr>
            <Table.Th>{t("reports.inventoryScan.overdue.itemId")}</Table.Th>
            <Table.Th>{t("reports.inventoryScan.overdue.product")}</Table.Th>
            <Table.Th>{t("reports.inventoryScan.overdue.site")}</Table.Th>
            <Table.Th>{t("reports.inventoryScan.overdue.status")}</Table.Th>
            {showDaysSinceLastScan && (
              <Table.Th className="text-right">
                {t("reports.inventoryScan.overdue.daysSinceLastScan")}
              </Table.Th>
            )}
            <Table.Th className="text-right">
              {t("reports.inventoryScan.overdue.estValue")}
            </Table.Th>
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {items.map((item, index) => (
            <Table.Tr
              key={item.itemId}
              data-overflow-row={index >= DEFAULT_VISIBLE ? "true" : undefined}
              className={!expanded && index >= DEFAULT_VISIBLE ? "hidden" : ""}
            >
              <Table.Td className="font-mono text-sm">
                <ReportLink href={`/inventory/items/${item.itemId}`}>
                  {item.externalId || `#${item.itemId}`}
                </ReportLink>
              </Table.Td>
              <Table.Td>
                {item.productId ? (
                  <ReportLink href={`/inventory/categories/${item.productId}`}>
                    {item.productName || item.productSku || "—"}
                  </ReportLink>
                ) : (
                  item.productName || item.productSku || "—"
                )}
              </Table.Td>
              <Table.Td>
                {item.siteId ? (
                  <ReportLink href={`/sites/${item.siteId}`}>{item.siteName}</ReportLink>
                ) : (
                  item.siteName || "—"
                )}
              </Table.Td>
              <Table.Td>
                {item.status ? sanitizeItemStatusLabel(item.status) : "—"}
              </Table.Td>
              {showDaysSinceLastScan && (
                <Table.Td className="text-right font-semibold text-red-600">
                  {item.daysSinceLastScan !== null ? `${item.daysSinceLastScan}d` : "—"}
                </Table.Td>
              )}
              <Table.Td className="text-right">
                {formatCurrency(item.estimatedValue)}
              </Table.Td>
            </Table.Tr>
          ))}
        </Table.Tbody>
      </Table>
      {hasMore && (
        <Button
          variant="light"
          color="gray"
          size="xs"
          fullWidth
          data-table-toggle
          classNames={{ label: "text-gray-900" }}
          rightSection={
            expanded ? (
              <IconChevronUp size={14} className="text-gray-900" />
            ) : (
              <IconChevronDown size={14} className="text-gray-900" />
            )
          }
          onClick={() => setExpanded((prev) => !prev)}
        >
          {expanded
            ? t("common.hideAll")
            : t("reports.inventoryScan.overdue.showAll", { total: items.length })}
        </Button>
      )}
    </div>
  );
};

export default CollapsibleItemTable;
